"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // z-[100] (was z-50): trader order sheets render into the same
      // #mobile-frame portal at z-[55] / z-[70]. With Dialog at z-50 a
      // session-expired (or account-blocked / settings-updated) modal opens
      // behind the open sheet, the user sees only the inline error from
      // setError(data.message), and 1.5s later the page redirects to
      // /trade/login — looks like a silent auto-logout.
      //
      // data-[state=closed]:pointer-events-none: during the ~200ms
      // close-out animation the overlay still has pointer-events:auto by
      // default, so a quick tap on the page header back button right after
      // hitting Cancel/X lands on the closing overlay (no-op) instead of
      // the button — user has to tap a second time. Drop pointer-events on
      // close so the back tap goes straight to its target.
      //
      // backdrop-blur deliberately omitted: `backdrop-filter: blur(...)`
      // is GPU-expensive — every frame the underlying content changes the
      // compositor must re-blur the viewport behind the overlay. That cost
      // compounds when a long-lived modal (e.g. AddFunds "Waiting for
      // payment" stage) sits over a page that ticks live values at 2 Hz,
      // and the whole UI feels sluggish. `bg-black/40` alone provides the
      // dim-out signal without the per-frame composite cost.
      "fixed inset-0 z-[100] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:pointer-events-none",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean
    usePortal?: boolean
  }
>(({ className, children, hideClose, usePortal = true, ...props }, ref) => {
  const portalNode = typeof window !== "undefined" ? document.getElementById("mobile-frame") : null

  const content = (
    <>
      <DialogOverlay className={cn(!usePortal && "absolute")} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          usePortal ? "fixed" : "absolute",
          // z-[100] (was z-50): must clear order sheets at z-[55] / z-[70] in
          // the same #mobile-frame portal — see DialogOverlay comment above.
          // data-[state=closed]:pointer-events-none mirrors the overlay
          // guard — the closing content panel shouldn't capture clicks
          // either while its zoom/fade-out animation plays out.
          "left-[50%] top-[50%] z-[100] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 max-h-[85vh] overflow-y-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] data-[state=closed]:pointer-events-none rounded-2xl",
          className
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </>
  )

  if (!usePortal) return content

  return (
    <DialogPortal container={portalNode}>
      {content}
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-normal leading-none tracking-tight", className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
